import {
  createFileRoute,
  isRedirect,
  Outlet,
  redirect,
  useNavigate,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { authClient } from "@/lib/auth-client";
import { useSync } from "@/lib/sync/use-sync";

let cachedSession: unknown = null;

export const Route = createFileRoute("/_settings")({
  beforeLoad: async () => {
    if (cachedSession) return;

    try {
      const session = await authClient.getSession();
      if (session.data) {
        cachedSession = session.data;
      } else {
        throw redirect({ to: "/auth/$path", params: { path: "sign-in" } });
      }
    } catch (e) {
      if (isRedirect(e)) throw e;
      if (!cachedSession) {
        throw redirect({ to: "/auth/$path", params: { path: "sign-in" } });
      }
    }
  },
  component: SettingsLayout,
});

function SettingsLayout() {
  const { data: session } = authClient.useSession();
  useSync(session?.user.accountId);
  const navigate = useNavigate();
  const router = useRouter();
  const title = useRouterState({
    select: (s) => s.matches.at(-1)?.staticData.title ?? "Settings",
  });

  if (!session) {
    cachedSession = null;
  }

  return (
    <div className="flex min-h-svh flex-col">
      <header className="sticky top-0 z-10 flex items-center gap-3 border-b bg-background px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
        <button
          type="button"
          // Settings can be opened from any tab (via the profile menu), so
          // return to wherever the user came from rather than a fixed
          // destination. Fall back to Shopping only when there's no local
          // history to pop (e.g. a direct deep link).
          onClick={() => {
            if (router.history.canGoBack()) {
              router.history.back();
            } else {
              navigate({ to: "/shopping" });
            }
          }}
          className="rounded-md p-1 -ml-1 active:bg-muted"
          aria-label="Back"
        >
          <ArrowLeft className="size-5" />
        </button>
        <h1 className="font-heading text-lg font-semibold tracking-tight">
          {title}
        </h1>
      </header>
      <main className="flex-1 overflow-y-auto p-4">
        <Outlet />
      </main>
    </div>
  );
}
